<script setup>
import { computed, nextTick, onMounted, onUpdated } from "vue"

// Self-reference so recursive <TreeNode> works
defineOptions({ name: 'TreeNode' })

const props = defineProps({
    node:           { type: Object,  required: true },
    isRoot:         { type: Boolean, default: false },
    parentId:       { type: Number,  default: null  },
    position:       { type: String,  default: null  },   // 'left' | 'right'
    loadingNodes:   { type: Object,  default: () => ({}) },
    animatingNodes: { type: Object,  default: () => ({}) },
    currencySymbol: { type: String,  default: '$'   },
})

const emit = defineEmits(['node-click', 'node-info', 'connectors-update'])

const isLoading   = computed(() => props.loadingNodes[props.node.id])
const isAnimating = computed(() => props.animatingNodes[props.node.id])

const hasLeft  = computed(() => !!props.node.left_child)
const hasRight = computed(() => !!props.node.right_child)
const isExpanded = computed(() => props.node._expanded !== false)

// Avatar initials
const initials = computed(() => {
    const n = props.node.display_name || props.node.name || ''
    return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'
})

const nodeTitle = computed(() => props.node.display_name || props.node.name || 'Unknown node')

// Status colours
const statusRing = computed(() =>
    props.node.status === 'active'
        ? 'ring-green-400/60 shadow-green-400/20'
        : 'ring-red-400/50 shadow-red-400/15'
)
const statusDot = computed(() =>
    props.node.status === 'active' ? 'bg-green-400' : 'bg-red-400'
)

// Volume formatting
const fmt = v => {
    const n = parseFloat(v) || 0
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
    if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
    return n.toFixed(0)
}

// After mount/update, tell parent to redraw connectors
onMounted(  () => nextTick(() => emit('connectors-update')))
onUpdated(  () => nextTick(() => emit('connectors-update')))
</script>

<template>
    <!-- ── Column layout: children wrap below the card ─────────────────────── -->
    <div class="node-col" :class="{ 'node-col--root': isRoot }">

        <!-- ── Node card ─────────────────────────────────────────────────────── -->
        <div
            :data-node-id="node.id"
            :data-parent-id="parentId ?? undefined"
            :data-position="position ?? undefined"
            class="node-card"
            :class="{
                'node-card--root':     isRoot,
                'node-card--active':   node.status === 'active',
                'node-card--inactive': node.status !== 'active',
                'node-card--loading':  isLoading,
                'node-card--animate':  isAnimating,
            }"
            @click.stop="emit('node-click', node)"
        >
            <!-- Status glow ring on avatar -->
            <div class="relative mx-auto mb-2" style="width:3.25rem;height:3.25rem;">
                <div :class="['absolute inset-0 rounded-full ring-2 shadow-md', statusRing]"></div>
                <div class="absolute inset-0.5 rounded-full overflow-hidden">
                    <img v-if="node.avatar_url" :src="node.avatar_url" :alt="node.name"
                         class="w-full h-full object-cover"/>
                    <div v-else
                         class="w-full h-full flex items-center justify-center text-sm font-black text-white"
                         :class="node.status === 'active'
                             ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                             : 'bg-gradient-to-br from-slate-500 to-slate-700'">
                        {{ initials }}
                    </div>
                </div>
                <!-- Status dot -->
                <span :class="['absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800 shadow-sm', statusDot,
                               node.status === 'active' ? 'animate-pulse' : '']"></span>
                <!-- Loading spinner overlay -->
                <div v-if="isLoading"
                     class="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <svg class="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                </div>
            </div>

            <!-- Name + UID -->
            <p class="node-name">{{ nodeTitle }}</p>
            <p class="node-uid">{{ node.uid ? `#${node.uid}` : '—' }}</p>

            <div class="mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.18em]"
                 :class="node.status === 'active'
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400'">
                <span class="w-1.5 h-1.5 rounded-full" :class="node.status === 'active' ? 'bg-green-500' : 'bg-red-500'"></span>
                {{ node.status === 'active' ? 'Active' : 'Inactive' }}
            </div>

            <!-- Package badge -->
            <div v-if="node.package_amount && parseFloat(node.package_amount) > 0"
                 class="node-badge">
                {{ currencySymbol }}{{ fmt(node.package_amount) }}
            </div>

            <!-- Volume strip -->
            <div class="node-vols">
                <div class="node-vol node-vol--left">
                    <span class="node-vol-label">L</span>
                    <span class="node-vol-value">{{ fmt(node.left_leg_volume) }}</span>
                </div>
                <div class="node-vol-divider"></div>
                <div class="node-vol node-vol--right">
                    <span class="node-vol-label">R</span>
                    <span class="node-vol-value">{{ fmt(node.right_leg_volume) }}</span>
                </div>
            </div>

            <!-- Action row: details button + expand chevron -->
            <div class="node-actions">
                <button class="node-btn-info" @click.stop="emit('node-info', node)" title="View Details">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </button>

                <button v-if="node.has_children || hasLeft || hasRight"
                        class="node-btn-expand"
                        :class="{ 'node-btn-expand--open': isExpanded && (hasLeft || hasRight) }"
                        @click.stop="emit('node-click', node)" title="Toggle children">
                    <svg class="w-3.5 h-3.5 transition-transform duration-300"
                         :class="isExpanded && (hasLeft || hasRight) ? 'rotate-180' : ''"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- ── Children row ──────────────────────────────────────────────────── -->
        <Transition name="children-expand">
            <div v-if="isExpanded && (hasLeft || hasRight || node.has_children)"
                 class="node-children">

                <!-- Left child or empty slot -->
                <div class="node-branch node-branch--left">
                    <TreeNode
                        v-if="hasLeft"
                        :node="node.left_child"
                        :parent-id="node.id"
                        position="left"
                        :loading-nodes="loadingNodes"
                        :animating-nodes="animatingNodes"
                        :currency-symbol="currencySymbol"
                        @node-click="$emit('node-click', $event)"
                        @node-info="$emit('node-info', $event)"
                        @connectors-update="$emit('connectors-update')"
                    />
                    <div v-else
                         class="node-empty"
                         :data-node-id="`empty-left-${node.id}`"
                         :data-parent-id="node.id"
                         data-position="left">
                        <div class="node-empty-icon">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                            </svg>
                        </div>
                        <p class="node-empty-label">Left Slot</p>
                        <p class="node-empty-sub">Open</p>
                    </div>
                </div>

                <!-- Right child or empty slot -->
                <div class="node-branch node-branch--right">
                    <TreeNode
                        v-if="hasRight"
                        :node="node.right_child"
                        :parent-id="node.id"
                        position="right"
                        :loading-nodes="loadingNodes"
                        :animating-nodes="animatingNodes"
                        :currency-symbol="currencySymbol"
                        @node-click="$emit('node-click', $event)"
                        @node-info="$emit('node-info', $event)"
                        @connectors-update="$emit('connectors-update')"
                    />
                    <div v-else
                         class="node-empty"
                         :data-node-id="`empty-right-${node.id}`"
                         :data-parent-id="node.id"
                         data-position="right">
                        <div class="node-empty-icon">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                            </svg>
                        </div>
                        <p class="node-empty-label">Right Slot</p>
                        <p class="node-empty-sub">Open</p>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style>
/* ── Column / layout ─────────────────────────────────────────────────────────── */
.node-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
}
.node-col--root > .node-card { margin-bottom: 0; }

/* ── Card ────────────────────────────────────────────────────────────────────── */
.node-card {
    position: relative;
    width: 9.5rem;
    padding: 0.875rem 0.75rem 0.65rem;
    border-radius: 1rem;
    text-align: center;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    background: white;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05);
}
.dark .node-card {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 2px 16px rgba(0,0,0,0.35), 0 1px 4px rgba(0,0,0,0.2);
}
.node-card--root {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.15), 0 4px 20px rgba(99,102,241,0.15);
}
.dark .node-card--root { border-color: #818cf8; box-shadow: 0 0 0 3px rgba(129,140,248,0.2), 0 4px 20px rgba(129,140,248,0.15); }
.node-card:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 6px 24px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.08);
    z-index: 10;
}
.dark .node-card:hover { box-shadow: 0 6px 28px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3); }
.node-card--active  { border-top: 2.5px solid #22c55e; }
.node-card--inactive { border-top: 2.5px solid #ef4444; }
.node-card--loading { opacity: 0.8; pointer-events: none; }
.node-card--animate { animation: card-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }

@keyframes card-pop {
    from { transform: scale(0.85); opacity: 0.5; }
    to   { transform: scale(1);    opacity: 1;   }
}

/* ── Name / UID / badge ─────────────────────────────────────────────────────── */
.node-name {
    font-size: 0.7rem;
    font-weight: 800;
    line-height: 1.2;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0.1rem;
}
.dark .node-name { color: #f1f5f9; }

.node-uid {
    font-size: 0.6rem;
    font-weight: 600;
    color: #94a3b8;
    font-family: monospace;
    margin-bottom: 0.4rem;
}

.node-badge {
    display: inline-block;
    font-size: 0.6rem;
    font-weight: 800;
    padding: 0.15rem 0.5rem;
    border-radius: 9999px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    margin-bottom: 0.45rem;
    letter-spacing: 0.02em;
    box-shadow: 0 1px 4px rgba(99,102,241,0.3);
}

/* ── Volume strip ───────────────────────────────────────────────────────────── */
.node-vols {
    display: flex;
    align-items: center;
    gap: 0;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 1px solid #f1f5f9;
    margin-bottom: 0.45rem;
    background: #f8fafc;
}
.dark .node-vols { border-color: #334155; background: #0f172a; }

.node-vol {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.25rem 0;
}
.node-vol-divider { width: 1px; height: 1.5rem; background: #e2e8f0; }
.dark .node-vol-divider { background: #334155; }

.node-vol-label {
    font-size: 0.55rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1;
    margin-bottom: 0.1rem;
}
.node-vol--left  .node-vol-label { color: #16a34a; }
.node-vol--right .node-vol-label { color: #9333ea; }
.dark .node-vol--left  .node-vol-label { color: #4ade80; }
.dark .node-vol--right .node-vol-label { color: #c084fc; }

.node-vol-value {
    font-size: 0.65rem;
    font-weight: 900;
    line-height: 1;
    font-family: monospace;
}
.node-vol--left  .node-vol-value { color: #15803d; }
.node-vol--right .node-vol-value { color: #7e22ce; }
.dark .node-vol--left  .node-vol-value { color: #86efac; }
.dark .node-vol--right .node-vol-value { color: #d8b4fe; }

/* ── Action row ─────────────────────────────────────────────────────────────── */
.node-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
}

.node-btn-info {
    display: flex; align-items: center; justify-content: center;
    width: 1.5rem; height: 1.5rem; border-radius: 0.35rem;
    background: #eff6ff; color: #3b82f6; border: 1px solid #bfdbfe;
    cursor: pointer; transition: all 0.15s ease;
    font-size: 0; /* hide text if any */
}
.dark .node-btn-info { background: #1e3a5f; border-color: #3b82f6; color: #93c5fd; }
.node-btn-info:hover { background: #3b82f6; color: white; border-color: #3b82f6; transform: scale(1.1); }

.node-btn-expand {
    display: flex; align-items: center; justify-content: center;
    width: 1.5rem; height: 1.5rem; border-radius: 0.35rem;
    background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0;
    cursor: pointer; transition: all 0.15s ease;
}
.dark .node-btn-expand { background: #0f172a; border-color: #334155; color: #94a3b8; }
.node-btn-expand:hover { background: #6366f1; color: white; border-color: #6366f1; transform: scale(1.1); }
.node-btn-expand--open { background: #eef2ff; color: #4f46e5; border-color: #a5b4fc; }
.dark .node-btn-expand--open { background: #1e1b4b; color: #a5b4fc; border-color: #4f46e5; }

/* ── Children row ───────────────────────────────────────────────────────────── */
.node-children {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 2.5rem;
    padding-top: 2.5rem;
}

.node-branch { display: flex; flex-direction: column; align-items: center; }

/* ── Expand / collapse transition ───────────────────────────────────────────── */
.children-expand-enter-active { transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1); }
.children-expand-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.children-expand-enter-from   { opacity: 0; transform: translateY(-12px) scale(0.95); }
.children-expand-leave-to     { opacity: 0; transform: translateY(-8px) scale(0.97); }

/* ── Empty slot ─────────────────────────────────────────────────────────────── */
.node-empty {
    width: 9.5rem;
    padding: 1rem 0.75rem;
    border-radius: 1rem;
    border: 1.5px dashed #cbd5e1;
    background: rgba(248,250,252,0.6);
    text-align: center;
    transition: all 0.18s ease;
    cursor: default;
}
.dark .node-empty {
    border-color: #334155;
    background: rgba(15,23,42,0.5);
}
.node-empty:hover {
    border-color: #6366f1;
    background: rgba(238,242,255,0.8);
    transform: translateY(-2px);
}
.dark .node-empty:hover { background: rgba(30,27,75,0.6); }

.node-empty-icon {
    width: 2rem; height: 2rem; border-radius: 0.5rem;
    margin: 0 auto 0.5rem;
    display: flex; align-items: center; justify-content: center;
    background: #f1f5f9; color: #94a3b8;
    border: 1px dashed #cbd5e1;
    transition: all 0.18s ease;
}
.dark .node-empty-icon { background: #1e293b; color: #475569; border-color: #334155; }
.node-empty:hover .node-empty-icon { background: #eef2ff; color: #6366f1; border-color: #a5b4fc; }

.node-empty-label {
    font-size: 0.65rem; font-weight: 800; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.06em;
    margin-bottom: 0.1rem;
}
.dark .node-empty-label { color: #475569; }

.node-empty-sub {
    font-size: 0.6rem; font-weight: 600; color: #22c55e;
}
.dark .node-empty-sub { color: #16a34a; }
</style>
